import { entityName as titleOf, type Entity, type NodeType, type StoryNode } from '@dispositio/core';
import type {
  AssetDto,
  AuthConfig,
  BeatTemplateDto,
  BeatTemplateSaveInput,
  ChangeSet,
  ConnectionCreateInput,
  ConnectionUpdateInput,
  DocumentCreateInput,
  DocumentUpdateInput,
  EntityCreateInput,
  EntityUpdateInput,
  StatementInput,
  GroupCreateInput,
  GroupUpdateInput,
  LibraryBrowse,
  LibraryImportInput,
  LibraryImportResult,
  MeResponse,
  NodeCreateInput,
  NodeUpdateInput,
  ProjectCreateInput,
  ProjectDto,
  ProjectListItem,
  SeriesDto,
  SeriesLibraryDto,
  SnapshotCreateInput,
  SnapshotDto,
  SnapshotTextDto,
  StoryPayload,
  TypeCreateInput,
  TypeUpdateInput,
} from '@dispositio/shared';
import { invertStep, stepOf } from '@dispositio/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { announceChange, onRemoteChange } from '../lib/storySync';
import { useEffect, useMemo } from 'react';
import { applyChanges, applyTypeChange, optimisticNode } from '../lib/changes';
import { de } from '../i18n/de';
import { useHistory } from '../state/history';
import { toast } from '../state/toast';
import { api, ApiError, errorMessage, setCsrfToken } from './client';

export const qk = {
  me: ['me'] as const,
  projects: ['projects'] as const,
  series: ['series'] as const,
  beatTemplates: ['beat-templates'] as const,
  story: (id: string) => ['story', id] as const,
};

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/** Öffentliche Angaben für Startseite, Anmeldung und Registrierung (wer sich registrieren darf, ob „Passwort vergessen“ geht). */
export const useAuthConfig = () => useQuery({ queryKey: ['auth-config'], queryFn: () => api.get<AuthConfig>('/auth/config'), staleTime: Infinity });

export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        const me = await api.get<MeResponse>('/auth/me');
        setCsrfToken(me.csrfToken);
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/** Beim Wechsel/Ende der Sitzung alle nutzerbezogenen Daten verwerfen, die laufende `me`-Abfrage aber behalten. */
function dropUserData(qc: QueryClient): void {
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
}

export function useAuthMutations() {
  const qc = useQueryClient();
  const done = (me: MeResponse): void => {
    setCsrfToken(me.csrfToken);
    dropUserData(qc);
    qc.setQueryData(qk.me, me);
  };
  const ended = (): void => {
    setCsrfToken('');
    dropUserData(qc);
    qc.setQueryData(qk.me, null);
  };
  return {
    login: useMutation({ mutationFn: (v: { email: string; password: string }) => api.post<MeResponse>('/auth/login', v), onSuccess: done }),
    register: useMutation({ mutationFn: (v: { email: string; password: string; inviteCode?: string }) => api.post<MeResponse>('/auth/register', v), onSuccess: done }),
    /** Entwicklung (DEV_AUTO_LOGIN): nach dem Abmelden wieder als Demo-Konto */
    devLogin: useMutation({ mutationFn: () => api.post<MeResponse>('/auth/dev-login'), onSuccess: done }),
    logout: useMutation({ mutationFn: () => api.post<void>('/auth/logout'), onSuccess: ended }),
    requestReset: useMutation({ mutationFn: (email: string) => api.post<{ ok: true }>('/auth/password-reset/request', { email }) }),
    confirmReset: useMutation({ mutationFn: (v: { token: string; password: string }) => api.post<void>('/auth/password-reset/confirm', v) }),
    deleteAccount: useMutation({ mutationFn: (password: string) => api.delete<void>('/account', { password }), onSuccess: ended }),
  };
}

/** Profilfoto setzen bzw. entfernen; der neue Stand landet gleich in `me` (Kopfleiste und Profil zeigen ihn sofort). */
export function useAvatarMutations() {
  const qc = useQueryClient();
  const setAvatar = (avatar: string | null): void => {
    qc.setQueryData<MeResponse | null>(qk.me, (old) => (old ? { ...old, user: { ...old.user, avatar } } : old));
  };
  return {
    upload: useMutation({ mutationFn: (dataUrl: string) => api.put<{ avatar: string }>('/auth/avatar', { dataUrl }), onSuccess: (r) => setAvatar(r.avatar) }),
    remove: useMutation({ mutationFn: () => api.delete<void>('/auth/avatar'), onSuccess: () => setAvatar(null) }),
  };
}

// ---------------------------------------------------------------------------
// Projekte
// ---------------------------------------------------------------------------

export const useProjects = () => useQuery({ queryKey: qk.projects, queryFn: () => api.get<ProjectListItem[]>('/projects') });

export function useProjectMutations() {
  const qc = useQueryClient();
  const inv = (): Promise<void> => qc.invalidateQueries({ queryKey: qk.projects });
  return {
    create: useMutation({ mutationFn: (v: ProjectCreateInput) => api.post<ProjectDto>('/projects', v), onSuccess: inv }),
    remove: useMutation({ mutationFn: (id: string) => api.delete<void>(`/projects/${id}`), onSuccess: inv }),
    /** Neues Projekt aus einer Projekt-Sicherung (ZIP, PLAN.md §29) */
    importBackup: useMutation({ mutationFn: (file: File) => api.upload<ProjectDto>('/projects/import/zip', new Blob([file], { type: 'application/zip' })), onSuccess: inv }),
    /** Tutorial beginnen: ein frisches Projekt über seinen Server-Aufruf (ersetzt ein vorhandenes Projekt dieses Tutorials) */
    startTutorial: useMutation({ mutationFn: (path: string) => api.post<ProjectDto>(path), onSuccess: inv }),
  };
}

/**
 * Abgehakte Hinweise des Prüfberichts (PF 28): gemerkt am Projekt (`dismissedIssues`), sofort im Stand der Geschichte gesetzt; scheitert
 * das Speichern, lädt die Geschichte neu.
 */
export function useCheckedIssues(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.patch<ProjectDto>(`/projects/${projectId}`, { dismissedIssues: ids }),
    onMutate: (ids) => {
      qc.setQueryData<StoryPayload>(qk.story(projectId), (s) => (s ? { ...s, project: { ...s.project, dismissedIssues: ids } } : s));
    },
    onSuccess: () => announceChange(projectId),
    onError: (e) => {
      toast.error(errorMessage(e));
      void qc.invalidateQueries({ queryKey: qk.story(projectId) });
    },
  });
}

/** Serien: Gruppen der Geschichten in der Übersicht (jedes Konto hat mindestens eine) */
export const useSeries = () => useQuery({ queryKey: qk.series, queryFn: () => api.get<SeriesDto[]>('/series') });

export function useSeriesMutations() {
  const qc = useQueryClient();
  const inv = async (): Promise<void> => {
    await Promise.all([qc.invalidateQueries({ queryKey: qk.series }), qc.invalidateQueries({ queryKey: qk.projects })]);
  };
  return {
    create: useMutation({ mutationFn: (name: string) => api.post<SeriesDto>('/series', { name }), onSuccess: inv }),
    rename: useMutation({ mutationFn: (v: { id: string; name: string }) => api.patch<SeriesDto>(`/series/${v.id}`, { name: v.name }), onSuccess: inv }),
    /** eine leere Serie; mit Geschichten nur mit `withStories` (vorher nachfragen) – dann gehen sie mit */
    remove: useMutation({ mutationFn: (v: { id: string; withStories: boolean }) => api.delete(`/series/${v.id}${v.withStories ? '?withStories=true' : ''}`), onSuccess: inv }),
    /** Reihenfolge der Episoden einer Serie (die Übersicht zeigt sie sofort) */
    order: useMutation({
      mutationFn: (v: { seriesId: string; projectIds: string[] }) => api.put<void>(`/series/${v.seriesId}/order`, { projectIds: v.projectIds }),
      onMutate: (v) => {
        qc.setQueryData<ProjectListItem[]>(qk.projects, (list) =>
          list?.map((p) => {
            const i = v.projectIds.indexOf(p.id);
            return i >= 0 ? { ...p, episode: i + 1 } : p;
          }),
        );
      },
      onSettled: () => qc.invalidateQueries({ queryKey: qk.projects }),
    }),
    /** Geschichte in eine andere Serie (die Übersicht zeigt sie sofort dort) */
    move: useMutation({
      mutationFn: (v: { projectId: string; seriesId: string }) => api.patch<ProjectDto>(`/projects/${v.projectId}`, { seriesId: v.seriesId }),
      onMutate: (v) => {
        qc.setQueryData<ProjectListItem[]>(qk.projects, (list) => list?.map((p) => (p.id === v.projectId ? { ...p, seriesId: v.seriesId } : p)));
      },
      onSettled: () => qc.invalidateQueries({ queryKey: qk.projects }),
    }),
  };
}

/** Eigene Vorlagen für Beat-Listen (am Konto, für alle Geschichten; PLAN.md §42) */
export const useBeatTemplates = () => useQuery({ queryKey: qk.beatTemplates, queryFn: () => api.get<BeatTemplateDto[]>('/beat-templates') });

export function useBeatTemplateMutations() {
  const qc = useQueryClient();
  const inv = (): Promise<void> => qc.invalidateQueries({ queryKey: qk.beatTemplates });
  return {
    /** speichern; gleicher Name ersetzt die vorhandene */
    save: useMutation({ mutationFn: (v: BeatTemplateSaveInput) => api.post<BeatTemplateDto>('/beat-templates', v), onSuccess: inv }),
    remove: useMutation({ mutationFn: (id: string) => api.delete<void>(`/beat-templates/${id}`), onSuccess: inv }),
  };
}

/**
 * Bibliothek einer anderen eigenen Geschichte, für den Import (null: noch keine gewählt) – `into`: die Geschichte, in die importiert wird
 * (aus einer Episode derselben Serie gibt es Referenzen, schon vorhandene sind markiert)
 */
export const useLibraryBrowse = (projectId: string | null, into: string) =>
  useQuery({ queryKey: ['library', projectId, into], queryFn: () => api.get<LibraryBrowse>(`/projects/${projectId}/library?into=${into}`), enabled: projectId !== null, staleTime: 0 });

/** Die Originale einer Serie für das Fenster beim Anlegen einer Episode (PLAN.md §43; null: keine) */
export const useSeriesLibrary = (seriesId: string | null) =>
  useQuery({ queryKey: ['series-library', seriesId], queryFn: () => api.get<SeriesLibraryDto>(`/series/${seriesId}/library`), enabled: seriesId !== null, staleTime: 0 });

/** Einträge einer anderen Geschichte als Kopien übernehmen; danach lädt die Geschichte neu (neue Klassen, Felder und Kategorien). */
export function useLibraryImport(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: LibraryImportInput) => api.post<LibraryImportResult>(`/projects/${projectId}/library/import`, v),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: qk.story(projectId) }), qc.invalidateQueries({ queryKey: qk.projects })]);
    },
  });
}

// ---------------------------------------------------------------------------
// Versionen der Textdokumente (PLAN.md §28)
// ---------------------------------------------------------------------------

const snapshotsKey = (projectId: string) => ['snapshots', projectId] as const;

/** Alle Versionen der Geschichte ohne Text, die neueste zuerst */
export const useSnapshots = (projectId: string) =>
  useQuery({ queryKey: snapshotsKey(projectId), queryFn: () => api.get<SnapshotDto[]>(`/projects/${projectId}/snapshots`), staleTime: 60_000 });

/** Eine Version mit Text (ändert sich nicht mehr; null: keine gewählt) */
export const useSnapshotText = (id: string | null) =>
  useQuery({ queryKey: ['snapshot', id], queryFn: () => api.get<SnapshotTextDto>(`/snapshots/${id}`), enabled: id !== null, staleTime: Infinity });

/** Sichern, umbenennen, löschen; Fehler erscheinen als Meldung, das Ergebnis ist dann null */
export function useSnapshotActions(projectId: string) {
  const qc = useQueryClient();
  return useMemo(() => {
    const key = snapshotsKey(projectId);
    const run = async <T,>(fn: () => Promise<T>, update: (list: SnapshotDto[], result: T) => SnapshotDto[]): Promise<T | null> => {
      try {
        const result = await fn();
        qc.setQueryData<SnapshotDto[]>(key, (list) => update(list ?? [], result));
        return result;
      } catch (e) {
        toast.error(errorMessage(e));
        return null;
      }
    };
    return {
      create: (nodeId: string, docId: string, input: SnapshotCreateInput) =>
        run(
          () => api.post<SnapshotDto>(`/nodes/${nodeId}/documents/${docId}/snapshots`, input),
          (list, s) => [s, ...list],
        ),
      rename: (id: string, title: string) =>
        run(
          () => api.patch<SnapshotDto>(`/snapshots/${id}`, { title }),
          (list, s) => list.map((x) => (x.id === id ? s : x)),
        ),
      remove: (id: string) =>
        run(
          async () => {
            await api.delete(`/snapshots/${id}`);
            return true;
          },
          (list) => list.filter((x) => x.id !== id),
        ),
    };
  }, [qc, projectId]);
}

// ---------------------------------------------------------------------------
// Geschichte
// ---------------------------------------------------------------------------

export const useStory = (projectId: string) =>
  useQuery({
    queryKey: qk.story(projectId),
    queryFn: () => api.get<StoryPayload>(`/projects/${projectId}/story`),
    staleTime: Infinity,
    // ohne Verbindung (Funkloch) noch zweimal versuchen, bevor ein Fehler gilt
    retry: (count, e) => e instanceof ApiError && e.code === 'network' && count < 2,
  });

/**
 * Hält die Geschichte mit anderen Fenstern bzw. Geräten gleich: Meldet ein anderes Fenster eine Änderung (storySync) oder kehrt man in
 * dieses Fenster zurück (höchstens alle 5 Sekunden), wird sie neu geladen – erst wenn eigene Änderungen angekommen sind.
 */
export function useStorySync(projectId: string): void {
  const qc = useQueryClient();
  useEffect(() => {
    let last = Date.now();
    const reload = async (): Promise<void> => {
      await Promise.allSettled([...pending]);
      last = Date.now();
      void qc.invalidateQueries({ queryKey: qk.story(projectId) });
    };
    const offRemote = onRemoteChange((id) => {
      if (id === projectId) void reload();
    });
    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && Date.now() - last > 5000) void reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      offRemote();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [qc, projectId]);
}

export type NodePatch = NodeUpdateInput;

/**
 * Alle schreibenden Aktionen einer Geschichte. Die Antwort des Servers (ChangeSet) wird in den Zwischenspeicher eingearbeitet;
 * Fehler erscheinen als Hinweis, danach wird neu geladen, damit die Anzeige wieder dem Server entspricht.
 */
/** Laufende Änderungen je Node (siehe inOrder in useStoryActions) */
const queues = new Map<string, Promise<unknown>>();
/** Laufende Änderungen überhaupt: Rückgängig wartet, bis sie angekommen sind */
const pending = new Set<Promise<unknown>>();

/** Wie ein Schritt im Verlauf heißt */
type Verb = keyof typeof de.history.verbs;
/** Lage-Änderungen (verschieben, andocken, einordnen) – alles andere ändert Inhalte */
const MOVE_KEYS = new Set(['x', 'y', 'w', 'h', 'parentId', 'at', 'from', 'to', 'labelDy', 'rank', 'chapterId']);
const onlyMoves = (patch: object): boolean => Object.keys(patch).length > 0 && Object.keys(patch).every((k) => MOVE_KEYS.has(k));

/** Name des ersten Datensatzes, den eine Änderung berührt (für die Beschriftung im Verlauf) */
function touchedName(before: StoryPayload, cs: ChangeSet): string {
  const entityName = (e: Entity | undefined): string => {
    const type = before.types.find((t) => t.id === e?.typeId);
    return titleOf(e, type) || e?.refKey || '';
  };
  const nodeName = (n: StoryNode | undefined): string => {
    if (!n) return '';
    if (n.entityId) return entityName(before.entities.find((e) => e.id === n.entityId));
    const type = before.types.find((t) => t.id === n.typeId);
    return String(n.fields[type?.titleKey ?? 'title'] ?? n.fields.title ?? '').trim() || type?.name || '';
  };
  const node = cs.nodes[0] ?? before.nodes.find((n) => n.id === cs.removed.nodes[0]);
  if (node) return nodeName(node);
  const entity = cs.entities[0] ?? before.entities.find((e) => e.id === cs.removed.entities[0]);
  if (entity) return entityName(entity);
  const group = cs.groups[0] ?? before.groups.find((g) => g.id === cs.removed.groups[0]);
  if (group) return group.name;
  return de.history.connection;
}

export function useStoryActions(projectId: string) {
  const qc = useQueryClient();
  return useMemo(() => {
    const key = qk.story(projectId);
    const update = (fn: (s: StoryPayload) => StoryPayload): void => {
      qc.setQueryData<StoryPayload>(key, (old) => (old ? fn(old) : old));
    };
    const apply = (cs: ChangeSet): ChangeSet => {
      update((s) => applyChanges(s, cs));
      return cs;
    };
    /** Führt eine Server-Aktion aus; bei Fehler Hinweis, Neuladen und `null`. Andere Fenster erfahren von der Änderung (storySync). */
    const attempt = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        const out = await fn();
        announceChange(projectId);
        return out;
      } catch (e) {
        toast.error(errorMessage(e));
        void qc.invalidateQueries({ queryKey: key });
        return null;
      }
    };
    const story = (): StoryPayload | undefined => qc.getQueryData<StoryPayload>(key);
    /** Hinweis, wenn der Server zu einem neuen Verweis im Text eine Instanz auf der Fläche angelegt hat */
    /** Hinweis, wenn der Server zu einem Verweis im Text ein Objekt in die Szene gelegt oder ohne Verweis wieder herausgenommen hat */
    const announceInstances = (cs: ChangeSet): ChangeSet => {
      const s = story();
      if (!s) return cs;
      const nameOf = (n: StoryNode): string => {
        const e = s.entities.find((x) => x.id === n.entityId);
        const type = s.types.find((t) => t.id === e?.typeId);
        return titleOf(e, type) || e?.refKey || '?';
      };
      const made = cs.nodes.filter((n) => n.kind === 'instance' && !s.nodes.some((o) => o.id === n.id));
      if (made.length > 0) toast.info(de.refs.autoPlaced(made.map(nameOf)));
      const gone = s.nodes.filter((n) => n.kind === 'instance' && cs.removed.nodes.includes(n.id));
      if (gone.length > 0) toast.info(de.refs.autoRemoved(gone.map(nameOf)));
      return cs;
    };
    /** Hinweis, wenn der Server den Schauplatz einer Szene aus ihrem Draft gesetzt hat (PLAN.md §16a) */
    const announcePlace = (cs: ChangeSet): ChangeSet => {
      const s = story();
      if (!s) return cs;
      for (const n of cs.nodes) {
        const was = s.nodes.find((o) => o.id === n.id);
        const place = n.fields.place;
        if (!was || typeof place !== 'string' || place === was.fields.place) continue;
        const e = s.entities.find((x) => x.id === place);
        const type = s.types.find((t) => t.id === e?.typeId);
        toast.info(de.places.fromText(titleOf(e, type) || e?.refKey || '?'));
      }
      return cs;
    };
    /**
     * Änderung mit Eintrag im Verlauf (Memento): Stand vorher festhalten (vor der sofort sichtbaren Vorschau), Aktion ausführen, aus
     * der Antwort den Schritt bilden. `key`: gleiche Stelle kurz nacheinander (Tippen) wird ein Schritt.
     */
    const track = <T extends ChangeSet>(verb: Verb, key: string | null, run: () => Promise<T>): Promise<T | null> =>
      attempt(async () => {
        const before = story();
        const job = run();
        pending.add(job);
        try {
          const cs = await job;
          if (before) useHistory.getState().record(projectId, de.history.label(de.history.verbs[verb], touchedName(before, cs)), key, stepOf(before, cs));
          return cs;
        } finally {
          pending.delete(job);
        }
      });
    /** Anfragen je Schlüssel (Node) der Reihe nach ausführen */
    const inOrder = <T,>(id: string, run: () => Promise<T>): Promise<T> => {
      const prev = queues.get(id) ?? Promise.resolve();
      const next = prev.catch(() => undefined).then(run);
      queues.set(id, next);
      next
        .finally(() => {
          if (queues.get(id) === next) queues.delete(id);
        })
        .catch(() => undefined); // Fehler meldet der Aufrufer (attempt)
      return next;
    };

    return {
      createNode: (input: NodeCreateInput) => track('create', null, async () => apply(await api.post<ChangeSet>(`/projects/${projectId}/nodes`, input))),
      /** Änderung an einem Node; einfache Werte erscheinen sofort, der Server bestätigt oder korrigiert. */
      /** Ereignis (Zeitpunkt) ↔ Zeitraum umwandeln */
      updateNode: (id: string, patch: NodePatch) =>
        track(onlyMoves(patch) ? 'move' : 'change', patch.fields || patch.dynamicFields ? `node:${id}:${Object.keys(patch.fields ?? {}).sort().join(',')}:${patch.dynamicFields ? 'dyn' : ''}` : null, async () => {
          const current = story()?.nodes.find((n) => n.id === id);
          if (current) {
            const { timeline: _t, sectionId, ...rest } = patch;
            // ein Kapitel in einen anderen Abschnitt: sein Elternknoten (null: das Manuskript) – sonst stünde es kurz mit dem neuen Schlüssel am alten Ort
            const simple = sectionId === undefined ? rest : { ...rest, parentId: sectionId ?? story()?.nodes.find((n) => n.kind === 'manuscript')?.id ?? current.parentId };
            update((s) => ({ ...s, nodes: s.nodes.map((n): StoryNode => (n.id === id ? optimisticNode(n, simple as Parameters<typeof optimisticNode>[1]) : n)) }));
          }
          // Änderungen am selben Node nacheinander senden: sonst kann eine frühere Änderung, die später ankommt, eine spätere überschreiben
          return apply(announceInstances(await inOrder(id, () => api.patch<ChangeSet>(`/nodes/${id}`, patch))));
        }),
      /**
       * Zeitlinie verschieben; gekoppelte, die der Hauptzeitlinie folgen, rücken sofort mit (`followers`: ihre neue Lage x) –
       * der Server richtet sie endgültig aus.
       */
      moveTimeline: (id: string, pos: { x: number; y: number }, followers: { id: string; x: number }[] = []) =>
        track('move', null, async () => {
          const moved = new Map(followers.map((f) => [f.id, f.x]));
          update((s) => ({ ...s, nodes: s.nodes.map((n): StoryNode => (n.id === id ? { ...n, ...pos } : moved.has(n.id) ? { ...n, x: moved.get(n.id)! } : n)) }));
          return apply(await inOrder(id, () => api.patch<ChangeSet>(`/nodes/${id}`, pos)));
        }),
      deleteNode: (id: string) => track('remove', null, async () => apply(await api.delete<ChangeSet>(`/nodes/${id}`))),
      createEntity: (input: EntityCreateInput) => track('create', null, async () => apply(await api.post<ChangeSet>(`/projects/${projectId}/entities`, input))),
      updateEntity: (id: string, input: EntityUpdateInput) =>
        track(onlyMoves(input) ? 'move' : 'change', input.fields ? `entity:${id}:${Object.keys(input.fields).sort().join(',')}` : null, async () => {
          update((s) => ({
            ...s,
            entities: s.entities.map((e) =>
              e.id === id
                ? { ...e, fields: input.fields ? { ...e.fields, ...input.fields } : e.fields, terms: input.terms ?? e.terms, x: input.x !== undefined ? input.x : e.x, y: input.y !== undefined ? input.y : e.y }
                : e,
            ),
          }));
          return apply(await inOrder(id, () => api.patch<ChangeSet>(`/entities/${id}`, input)));
        }),
      deleteEntity: (id: string) => track('remove', null, async () => apply(await api.delete<ChangeSet>(`/entities/${id}`))),
      /** Eingabezeile in natürlicher Sprache (PLAN.md §16c): eine Beziehung bzw. Zustandsänderung, fehlende Elemente gleich mit */
      statement: (input: StatementInput) => track('change', null, async () => apply(await api.post<ChangeSet>(`/projects/${projectId}/statements`, input))),
      /** Textdokumente (.md) eines Ereignisses oder Zeitraums */
      createDocument: (nodeId: string, input: DocumentCreateInput) => track('create', null, async () => apply(await api.post<ChangeSet>(`/nodes/${nodeId}/documents`, input))),
      updateDocument: (nodeId: string, docId: string, input: DocumentUpdateInput) =>
        track(input.text !== undefined ? 'text' : 'change', input.text !== undefined ? `doc:${docId}` : null, async () => {
          update((s) => ({
            ...s,
            nodes: s.nodes.map((n) => (n.id === nodeId ? { ...n, documents: n.documents.map((d) => (d.id === docId ? { ...d, name: input.name ?? d.name, text: input.text ?? d.text, ignored: input.ignored ?? d.ignored } : d)) } : n)),
          }));
          return apply(announcePlace(announceInstances(await inOrder(nodeId, () => api.patch<ChangeSet>(`/nodes/${nodeId}/documents/${docId}`, input)))));
        }),
      deleteDocument: (nodeId: string, docId: string) => track('remove', null, async () => apply(await api.delete<ChangeSet>(`/nodes/${nodeId}/documents/${docId}`))),
      /** Gruppierungsrahmen der Bibliothek */
      createGroup: (input: GroupCreateInput) => track('create', null, async () => apply(await api.post<ChangeSet>(`/projects/${projectId}/groups`, input))),
      updateGroup: (id: string, input: GroupUpdateInput) =>
        track(Object.keys(input).every((k) => ['x', 'y', 'w', 'h', 'rank'].includes(k)) ? 'move' : 'change', input.name !== undefined ? `group:${id}:name` : null, async () => {
          update((s) => ({ ...s, groups: s.groups.map((g) => (g.id === id ? { ...g, ...input } : g)) }));
          return apply(await api.patch<ChangeSet>(`/groups/${id}`, input));
        }),
      deleteGroup: (id: string) => track('remove', null, async () => apply(await api.delete<ChangeSet>(`/groups/${id}`))),
      createType: (input: TypeCreateInput) =>
        attempt(async () => {
          const t = await api.post<NodeType>(`/projects/${projectId}/types`, input);
          update((s) => applyTypeChange(s, t));
          return t;
        }),
      updateType: (id: string, input: TypeUpdateInput) =>
        attempt(async () => {
          const t = await api.patch<NodeType>(`/types/${id}`, input);
          update((s) => applyTypeChange(s, t));
          return t;
        }),
      deleteType: (id: string) =>
        attempt(async () => {
          await api.delete<void>(`/types/${id}`);
          update((s) => applyTypeChange(s, null, id));
          return true as const;
        }),
      createConnection: (input: ConnectionCreateInput) => track('connect', null, async () => apply(await api.post<ChangeSet>(`/projects/${projectId}/connections`, input))),
      updateConnection: (id: string, input: ConnectionUpdateInput) =>
        track('change', input.label !== undefined ? `connection:${id}:label` : null, async () => {
          update((s) => ({ ...s, connections: s.connections.map((c) => (c.id === id ? { ...c, ...input } : c)) }));
          return apply(await api.patch<ChangeSet>(`/connections/${id}`, input));
        }),
      deleteConnection: (id: string) => track('remove', null, async () => apply(await api.delete<ChangeSet>(`/connections/${id}`))),
      /** Mehrere Aktionen einer Geste als ein Schritt im Verlauf (z. B. Kategorie samt ihrer Elemente verschieben); ohne `label` heißt er wie die erste */
      group: async <T,>(run: () => Promise<T>, label = ''): Promise<T> => {
        const h = useHistory.getState();
        h.beginGroup(label);
        try {
          return await run();
        } finally {
          useHistory.getState().endGroup();
        }
      },
      uploadAsset: (dataUrl: string) => attempt(() => api.post<AssetDto>(`/projects/${projectId}/assets`, { dataUrl })),
    };
  }, [qc, projectId]);
}

export type StoryActions = ReturnType<typeof useStoryActions>;

/**
 * Rückgängig und Wiederholen (Memento): Der oberste Schritt geht an den Server (`/history`), der prüft, ob die Geschichte noch auf dem
 * erwarteten Stand ist, und schreibt den Stand davor bzw. danach zurück. Neu laden (nach einem Fehler, aus einem anderen Fenster)
 * lässt den Verlauf stehen: Passt ein Schritt nicht mehr, lehnt der Server ihn ab, und erst dann beginnt der Verlauf leer.
 */
export function useStoryHistory(projectId: string) {
  const qc = useQueryClient();
  const undoTop = useHistory((s) => (s.projectId === projectId ? s.undo[s.undo.length - 1] : undefined));
  const redoTop = useHistory((s) => (s.projectId === projectId ? s.redo[s.redo.length - 1] : undefined));
  const busy = useHistory((s) => s.busy);
  useEffect(() => {
    useHistory.getState().use(projectId);
  }, [projectId]);
  const ops = useMemo(() => {
    const run = async (dir: 'undo' | 'redo'): Promise<void> => {
      const h = useHistory.getState();
      if (h.busy || h.projectId !== projectId) return;
      h.setBusy(true);
      try {
        // erst ankommen lassen, was noch unterwegs ist (sonst fehlte sein Schritt)
        await Promise.allSettled([...pending]);
        const now = useHistory.getState();
        const top = (dir === 'undo' ? now.undo : now.redo).at(-1);
        if (!top) return;
        const cs = await api.post<ChangeSet>(`/projects/${projectId}/history`, dir === 'undo' ? invertStep(top.step) : top.step);
        qc.setQueryData<StoryPayload>(qk.story(projectId), (old) => (old ? applyChanges(old, cs) : old));
        useHistory.getState().moved(dir);
        announceChange(projectId);
        toast.info(dir === 'undo' ? de.history.undone(top.label) : de.history.redone(top.label));
      } catch (e) {
        toast.error(errorMessage(e));
        useHistory.getState().clear();
        void qc.invalidateQueries({ queryKey: qk.story(projectId) });
      } finally {
        useHistory.getState().setBusy(false);
      }
    };
    return { undo: () => run('undo'), redo: () => run('redo') };
  }, [qc, projectId]);
  return { ...ops, undoLabel: undoTop?.label ?? null, redoLabel: redoTop?.label ?? null, busy };
}
